import type { ImageSourcePropType } from 'react-native';

const KNOWN_BUNDLED_AGENT_AVATAR_FILES = new Set([
  'Assistant.png',
  'BusinessDeveloper.png',
  'SalesMarketer.png',
  'Researcher.png',
]);

/**
 * Avatar for one agent-feed entry (remote URL, bundled filename, or type-based default).
 * Mirrors EasyMode LogMessage resolution so charts match the agent log.
 */
export function resolveAgentFeedEntryAvatar(entry: {
  user?: { avatar?: unknown };
  agentMeta?: { agentType?: string; agentId?: string };
} | null | undefined): ImageSourcePropType {
  if (!entry) {
    return getAgentAvatarSource(null);
  }

  const avatarValue = entry.user?.avatar;
  const normalizedAvatar = typeof avatarValue === 'string' ? avatarValue.trim() : '';
  const avatarFileName =
    normalizedAvatar.split('?')[0].split('#')[0].split('/').pop() || normalizedAvatar;
  const isKnownBundled = KNOWN_BUNDLED_AGENT_AVATAR_FILES.has(avatarFileName);
  const lower = avatarFileName.toLowerCase();
  const isOpenClaw = lower === 'openclaw' || lower === 'openclaw.png';

  if (normalizedAvatar && (isKnownBundled || isOpenClaw)) {
    return getAgentAvatarSource({
      avatar: avatarFileName,
      type: isOpenClaw ? 'openclaw' : undefined,
      _id: isOpenClaw ? 'openclaw' : undefined,
      isOpenClaw,
    });
  }
  if (normalizedAvatar) {
    return { uri: normalizedAvatar };
  }
  if (typeof avatarValue === 'number') {
    return avatarValue;
  }
  if (avatarValue && typeof avatarValue === 'object') {
    return avatarValue as ImageSourcePropType;
  }

  const agentType = entry.agentMeta?.agentType;
  const agentId = entry.agentMeta?.agentId;
  return getAgentAvatarSource(agentType || agentId ? { type: agentType, _id: agentId } : null);
}

/**
 * Shared helper to resolve agent avatar image source for display.
 * Used by EasyModeScreen (agent selector) and WelcomeScreen AI suggestions (agent attribution).
 */
export function getAgentAvatarSource(agent: { _id?: string; type?: string; avatar?: string; isOpenClaw?: boolean } | null | undefined): number {
  if (!agent) {
    return require('@assets/images/1x/Assistant.png');
  }

  const avatarMap: Record<string, number> = {
    'Assistant.png': require('@assets/images/1x/Assistant.png'),
    'BusinessDeveloper.png': require('@assets/images/1x/BusinessDeveloper.png'),
    'SalesMarketer.png': require('@assets/images/1x/SalesMarketer.png'),
    'Researcher.png': require('@assets/images/1x/Researcher.png'),
  };

  const typeMap: Record<string, number> = {
    'assistant': require('@assets/images/1x/Assistant.png'),
    'business_developer': require('@assets/images/1x/BusinessDeveloper.png'),
    'sales_marketer': require('@assets/images/1x/SalesMarketer.png'),
    'researcher': require('@assets/images/1x/Researcher.png'),
  };

  if (agent.avatar && avatarMap[agent.avatar]) {
    return avatarMap[agent.avatar];
  }

  if (agent.type && typeMap[agent.type]) {
    return typeMap[agent.type];
  }

  if (agent._id === 'openclaw' || agent.type === 'openclaw' || agent.isOpenClaw) {
    return require('@assets/openclaw.png');
  }

  return require('@assets/images/1x/Assistant.png');
}
